import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { postWebViewOutstandingMasterReportDataApiHandler } from "../../../../services/AdminIndex";
import {
  processOutstandingPartyRow,
  calcAmount,
  calcReceivedAmount,
  calcDiscountAmount,
  calcSrPrAmount,
  calcBalanceAmount,
  calcOddDays,
  sumPartyField,
  formatNumber,
  formatDateDisplay,
  isCustomerReport,
} from "../../../../utils/outstandingReportCalc";

// Headless-render-only page for the Auto Report Mail worker: Puppeteer
// navigates here, waits for #report-print-ready, then calls page.pdf().
// Not linked from anywhere in the app UI — this is not the interactive
// Outstanding Report screen (see outstandingReport/OutstandingReport.js),
// but it reuses that page's exact calc/merge logic (outstandingReportCalc.js)
// so the numbers here are never allowed to drift from the manual report.
const dueDateOf = (sale) => {
  if (!sale?.invoiceDate) return "";
  return moment(sale.invoiceDate).add(Number(sale?.creditDays) || 0, "days").format("DD-MM-YYYY");
};

const safeParseJson = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = typeof val === "string" ? JSON.parse(val) : val;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function BillTable({ reportType, party }) {
  const srPrLabel = isCustomerReport(reportType) ? "SR Amt." : "PR Amt.";
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 8 }}>
      <thead>
        <tr>
          {["Bill No.", "Bill Date", "Amount", "Received Amt.", "Discount Amt.", srPrLabel, "Balance Amt.", "Due Date", "O/D Days"].map((h) => (
            <th key={h} style={{ border: "1px solid #ccc", padding: "6px 8px", background: "#f4a261", textAlign: "left" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {party.salesData.map((sale, idx) => (
          <tr key={idx} style={{ background: idx % 2 ? "#ebf5fb" : "#fff" }}>
            <td style={cellStyle}>{sale.invoiceNo}</td>
            <td style={cellStyle}>{formatDateDisplay(sale.invoiceDate)}</td>
            <td style={cellStyle}>{formatNumber(calcAmount(sale, reportType))}</td>
            <td style={cellStyle}>{formatNumber(calcReceivedAmount(sale, reportType))}</td>
            <td style={cellStyle}>{formatNumber(calcDiscountAmount(sale))}</td>
            <td style={cellStyle}>{formatNumber(calcSrPrAmount(sale, reportType))}</td>
            <td style={cellStyle}>{formatNumber(calcBalanceAmount(sale, reportType))}</td>
            <td style={cellStyle}>{dueDateOf(sale)}</td>
            <td style={cellStyle}>{calcOddDays(sale)}</td>
          </tr>
        ))}
        <tr style={{ background: "#ffe9a8", fontWeight: "bold" }}>
          <td style={cellStyle} colSpan={2}>Party Total</td>
          <td style={cellStyle}>{formatNumber(sumPartyField(party.salesData, calcAmount, reportType))}</td>
          <td style={cellStyle}>{formatNumber(sumPartyField(party.salesData, calcReceivedAmount, reportType))}</td>
          <td style={cellStyle}>{formatNumber(sumPartyField(party.salesData, calcDiscountAmount, reportType))}</td>
          <td style={cellStyle}>{formatNumber(sumPartyField(party.salesData, calcSrPrAmount, reportType))}</td>
          <td style={cellStyle}>{formatNumber(sumPartyField(party.salesData, calcBalanceAmount, reportType))}</td>
          <td style={cellStyle}></td>
          <td style={cellStyle}></td>
        </tr>
      </tbody>
    </table>
  );
}

const cellStyle = { border: "1px solid #ddd", padding: "6px 8px" };

export default function PrintOutstandingReport() {
  const { companyId, accountId, tillDate, partyType } = useParams();
  const [parties, setParties] = useState([]);
  const [brokerSections, setBrokerSections] = useState([]);
  const [brokerName, setBrokerName] = useState("");
  const [ready, setReady] = useState(false);

  const [searchParams] = useSearchParams();
  const selectedYearStart = searchParams.get("selectedYearStart") || null;

  const isBroker = partyType === "broker";
  const reportType = partyType === "supplier" ? "supplier" : "customer";

  useEffect(() => {
    async function loadFlat() {
      const res = await postWebViewOutstandingMasterReportDataApiHandler({
        companyId,
        reportType,
        // Must match the interactive Outstanding Report's "Show" value —
        // getOutstandingReportSP/getOutstandingReport1SP only branch on
        // 'outstanding' | 'date' | 'all'; anything else (e.g. the display
        // label "Bill O/s") matches no branch and silently returns zero rows.
        billType: "outstanding",
        // Without this, the SP's own filter is "customerType IS NULL OR
        // customerType='all' OR (customerType='selected' AND party IN ...)"
        // — omitting it makes the whole clause true unconditionally and
        // ignores `party` entirely, returning every party in the company
        // instead of just this one.
        customerType: "selected",
        party: [accountId],
        tillDate,
        // Every bill-selection subquery in the SP is gated on
        // "(date BETWEEN selectedYearStart/End) OR (date < selectedYearStart
        // AND balance > 0)" — NULL here makes both branches UNKNOWN in SQL,
        // so passing null (as this used to) silently returns zero rows.
        selectedYearStart,
        selectedYearEnd: tillDate,
      });
      const rawRows = res?.data?.[0] || [];
      const processed = rawRows
        .map((r) => processOutstandingPartyRow(r, reportType))
        .filter(Boolean)
        .filter((p) => p.salesData.length > 0);
      setParties(processed);
    }

    async function loadBroker() {
      const fetchSection = (sectionReportType, sectionKey) =>
        postWebViewOutstandingMasterReportDataApiHandler({
          companyId,
          reportType: sectionReportType,
          billType: "outstanding",
          customerType: "all",
          brokerType: "selected",
          broker: accountId,
          tillDate,
          selectedYearStart,
          selectedYearEnd: tillDate,
        }).then((res) => {
          const row = res?.data?.[0]?.[0];
          if (!row) return null;
          const partiesRaw = safeParseJson(row.brokerSalesData);
          // Each broker-nested party's bill array comes back as
          // `salesDetails` (not `salesData` like the flat customer/
          // supplier SPs) — rename before processing so the exact same
          // merge/sort/calc logic applies uniformly.
          const parties = partiesRaw
            .map((p) => processOutstandingPartyRow({ ...p, accountName: p.partyName, salesData: p.salesDetails }, sectionKey))
            .filter(Boolean)
            .filter((p) => p.salesData.length > 0);
          return { brokerName: row.brokerName, parties };
        });

      const [salesSection, supplierSection] = await Promise.all([
        fetchSection("customerbroker", "customer"),
        fetchSection("supplierBroker", "supplier"),
      ]);

      const sections = [];
      if (salesSection?.parties?.length) {
        sections.push({ label: "Customer Outstanding (Broker-wise)", reportType: "customer", parties: salesSection.parties });
      }
      if (supplierSection?.parties?.length) {
        sections.push({ label: "Supplier Outstanding (Broker-wise)", reportType: "supplier", parties: supplierSection.parties });
      }
      setBrokerName(salesSection?.brokerName || supplierSection?.brokerName || "");
      setBrokerSections(sections);
    }

    (isBroker ? loadBroker() : loadFlat()).finally(() => setReady(true));
  }, [companyId, accountId, tillDate, partyType, selectedYearStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasData = isBroker ? brokerSections.length > 0 : parties.length > 0;

  return (
    <div style={{ fontFamily: "Arial, sans-serif", padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Outstanding Report</h2>
      <div style={{ marginBottom: 16, color: "#555" }}>
        {isBroker ? (brokerName ? `${brokerName} — ` : "") : (parties[0]?.accountName ? `${parties[0].accountName} — ` : "")}
        As on {tillDate}
      </div>

      {hasData ? (
        isBroker ? (
          brokerSections.map((section) => (
            <div key={section.label} style={{ marginBottom: 20 }}>
              <h3 style={{ background: "#8d99ae", color: "#fff", padding: "6px 8px", margin: "0 0 8px" }}>
                {section.label}
              </h3>
              {section.parties.map((party) => (
                <div key={party.partyId || party.accountName}>
                  <div style={{ background: "#d8f3dc", padding: "4px 8px", fontWeight: "bold" }}>
                    {party.accountName}
                  </div>
                  <BillTable reportType={section.reportType} party={party} />
                </div>
              ))}
            </div>
          ))
        ) : (
          parties.map((party) => (
            <div key={party.partyId || party.accountName}>
              <BillTable reportType={reportType} party={party} />
            </div>
          ))
        )
      ) : (
        <div>No outstanding data found for this period.</div>
      )}
      {ready ? <div id="report-print-ready" /> : null}
    </div>
  );
}
